import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaCalendarPlus,
  FaCheck,
  FaSearch,
  FaSpinner,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../services/axios";
import { marcarHorariosConteudo } from "../../services/conteudoSlots";
import "../adm/admPatrocinadoresList.css";
import "../adm/adm.css";
import "./incluirReuniaoNegocio.css";

const LIMITE_REUNIOES_CONVIDADO = 6;

function normalizarHora(valor) {
  if (!valor) return "";
  const texto = String(valor);
  return texto.length >= 5 ? texto.substring(0, 5) : texto;
}

function extrairDataHora(dt) {
  const texto = String(dt || "");
  return {
    data: texto.substring(0, 10),
    hora: texto.length >= 16 ? texto.substring(11, 16) : "",
  };
}

function contarOcupacaoPatrocinador(patrocinador, dataSelecionada, horariosDia) {
  const contagem = {};
  horariosDia.forEach((h) => {
    contagem[h] = 0;
  });

  (patrocinador?.usuarios || []).forEach((u) => {
    (u.agendaParticipante || []).forEach((ap) => {
      const agenda = ap.agenda;
      if (!agenda) return;
      if (agenda.tipo !== "reuniao_negocio") return;
      if (agenda.nome === "Reunião de almoço/jantar") return;

      const dt = agenda.dt_hora_inicio || agenda.dtHoraInicio;
      const { data, hora } = extrairDataHora(dt);
      if (data !== dataSelecionada) return;
      if (contagem[hora] === undefined) return;
      contagem[hora] += 1;
    });
  });

  return contagem;
}

function enriquecerParticipante(participante, ctx) {
  const { dataSelecionada, horariosDia, ocupacaoPatrocinador, cota, slots } =
    ctx;
  const indisponivel = new Set();
  const selectIndisponivel = new Set();
  const destinos = [indisponivel, selectIndisponivel];

  (participante.agendaParticipante || []).forEach((a) => {
    const agenda = a.agenda;
    if (!agenda) return;
    const dt = agenda.dt_hora_inicio || agenda.dtHoraInicio;
    const { data, hora } = extrairDataHora(dt);
    if (!data || data !== dataSelecionada || !hora) return;

    if (
      agenda.tipo === "conteudo_patrocinado" ||
      agenda.tipo === "conteudo_editorial"
    ) {
      // Conteúdo id 5 ocupa 3 slots (inclui 12:00); id 4 ocupa 2.
      marcarHorariosConteudo({
        idAgendaConteudo: agenda.id,
        horaInicio: hora,
        slots,
        dataSelecionada,
        horariosDia,
        destinos,
      });
      return;
    }

    indisponivel.add(hora);
    selectIndisponivel.add(hora);
  });

  // Preferências conteudo1/conteudo2 do convidado (mesma regra do backend)
  ;[participante.conteudo1, participante.conteudo2].forEach((idConteudo) => {
    if (!idConteudo) return;
    marcarHorariosConteudo({
      idAgendaConteudo: idConteudo,
      horaInicio: "",
      slots,
      dataSelecionada,
      horariosDia,
      destinos,
    });
  });

  if (cota) {
    horariosDia.forEach((h) => {
      if ((ocupacaoPatrocinador[h] || 0) >= cota) {
        selectIndisponivel.add(h);
      }
    });
  }

  const contadorReuniao = (participante.agendaParticipante || []).filter(
    (apc) =>
      apc.agenda?.tipo === "reuniao_negocio" &&
      apc.agenda?.nome !== "Reunião de almoço/jantar"
  ).length;

  const lotado = contadorReuniao >= LIMITE_REUNIOES_CONVIDADO;
  if (lotado) {
    horariosDia.forEach((h) => {
      indisponivel.add(h);
      selectIndisponivel.add(h);
    });
  }

  return {
    ...participante,
    horariosDisponiveis: horariosDia.filter((h) => !indisponivel.has(h)),
    horariosSelecionaveis: horariosDia.filter((h) => !selectIndisponivel.has(h)),
    lotado,
  };
}

const IncluirReuniaoNegocio = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [patrocinador, setPatrocinador] = useState(null);
  const [idsJaMarcados, setIdsJaMarcados] = useState([]);
  const [participantes, setParticipantes] = useState([]);
  const [diasEvento, setDiasEvento] = useState([]);
  const [slots, setSlots] = useState([]);
  const [dataSelecionada, setDataSelecionada] = useState("");
  const [pesquisaNome, setPesquisaNome] = useState("");
  const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);
  const [horarioSelecionado, setHorarioSelecionado] = useState("");
  const [isLoadingMeta, setIsLoadingMeta] = useState(true);
  const [isLoadingLista, setIsLoadingLista] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const cota =
    Number(
      patrocinador?.plano?.cota_simultania ??
        patrocinador?.plano?.cotaSimultania ??
        0
    ) || 0;

  const diasComSlots = useMemo(() => {
    const datas = [...new Set(slots.map((s) => s.data).filter(Boolean))].sort();
    return datas.map((data) => {
      const [, mes, dia] = String(data).split("-");
      const fromEvento = diasEvento.find((d) => d.data === data);
      return {
        data,
        dia: fromEvento?.dia || dia,
        label: fromEvento?.label || `${dia}/${mes}`,
      };
    });
  }, [slots, diasEvento]);

  const diaSelecionado = useMemo(() => {
    const encontrado = diasComSlots.find((d) => d.data === dataSelecionada);
    return encontrado?.dia || "";
  }, [diasComSlots, dataSelecionada]);

  const labelDiaAtivo = useMemo(() => {
    const d = diasComSlots.find((item) => item.data === dataSelecionada);
    return d?.label || "—";
  }, [diasComSlots, dataSelecionada]);

  const slotsDoDia = useMemo(() => {
    if (!dataSelecionada) return [];
    return slots
      .filter((s) => s.data === dataSelecionada)
      .slice()
      .sort((a, b) =>
        String(a.horario_inicio || "").localeCompare(
          String(b.horario_inicio || "")
        )
      );
  }, [slots, dataSelecionada]);

  const horariosDia = useMemo(
    () => slotsDoDia.map((s) => normalizarHora(s.horario_inicio)).filter(Boolean),
    [slotsDoDia]
  );

  const ocupacaoPatrocinador = useMemo(
    () => contarOcupacaoPatrocinador(patrocinador, dataSelecionada, horariosDia),
    [patrocinador, dataSelecionada, horariosDia]
  );

  const convidadoSelecionado = useMemo(
    () => participantes.find((p) => p.id === usuarioSelecionado) || null,
    [participantes, usuarioSelecionado]
  );

  const horariosSelecionaveis = useMemo(
    () => convidadoSelecionado?.horariosSelecionaveis || [],
    [convidadoSelecionado]
  );

  const listaFiltrada = useMemo(() => {
    const termo = pesquisaNome.trim().toLowerCase();
    return participantes.filter((p) => {
      if (idsJaMarcados.includes(p.id)) return false;
      if (termo && !(p.nome || "").toLowerCase().includes(termo)) return false;
      return true;
    });
  }, [participantes, idsJaMarcados, pesquisaNome]);

  const podeCriar =
    Boolean(usuarioSelecionado) &&
    Boolean(horarioSelecionado) &&
    Boolean(dataSelecionada) &&
    !isSaving;

  const carregarMeta = useCallback(async () => {
    if (!id) return;
    setIsLoadingMeta(true);
    setError(null);
    try {
      const [respDias, respSlots, respPatrocinador] = await Promise.all([
        api.get("/agenda-dias-evento"),
        api.get("/slots"),
        api.post("/patrocinador-verifica-disponibilidade", {
          id_info: Number(id),
        }),
      ]);

      const dias = respDias.data?.dados?.dias || [];
      const listaSlots = respSlots.data?.dados || [];
      const patro = respPatrocinador.data?.patrocinador || null;

      setDiasEvento(dias);
      setSlots(listaSlots);
      setPatrocinador(patro);

      const datasSlots = [
        ...new Set(listaSlots.map((s) => s.data).filter(Boolean)),
      ].sort();
      setDataSelecionada(datasSlots[0] || dias[0]?.data || "");

      const idUsuarioPatro = patro?.id_usuario ?? patro?.idUsuario;
      if (idUsuarioPatro) {
        const marcados = await api.post("/patrocinador-por-id-recepcao", {
          id_info: idUsuarioPatro,
        });
        setIdsJaMarcados(marcados.data?.agendasParticipantes || []);
      } else {
        setIdsJaMarcados([]);
      }
    } catch (err) {
      console.error("Erro ao carregar metadados:", err);
      setError("Erro ao carregar dias, slots e dados do patrocinador.");
      toast.error(
        err?.response?.data?.mensagem ||
          "Erro ao carregar dados para marcar reunião."
      );
    } finally {
      setIsLoadingMeta(false);
    }
  }, [id]);

  const carregarParticipantes = useCallback(async () => {
    if (!dataSelecionada || horariosDia.length === 0) {
      setParticipantes([]);
      return;
    }

    setIsLoadingLista(true);
    setError(null);
    try {
      const respUsuarios = await api.post(`/usuarios-apenas-reunioes`, {
        tipo: ["convidado"],
      });

      if (respUsuarios.status === 200) {
        const ocupacao = contarOcupacaoPatrocinador(
          patrocinador,
          dataSelecionada,
          horariosDia
        );
        const enriquecidos = (respUsuarios.data.dados || []).map((p) =>
          enriquecerParticipante(p, {
            dataSelecionada,
            horariosDia,
            ocupacaoPatrocinador: ocupacao,
            cota,
            slots,
          })
        );
        setParticipantes(enriquecidos);
      }
    } catch (err) {
      console.error("Erro ao carregar convidados:", err);
      setError("Erro ao carregar convidados disponíveis.");
      setParticipantes([]);
    } finally {
      setIsLoadingLista(false);
    }
  }, [dataSelecionada, horariosDia, patrocinador, cota, slots]);

  useEffect(() => {
    carregarMeta();
  }, [carregarMeta]);

  useEffect(() => {
    if (isLoadingMeta || !dataSelecionada) return;
    setUsuarioSelecionado(null);
    setHorarioSelecionado("");
    carregarParticipantes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSelecionada, isLoadingMeta, horariosDia.join("|")]);

  const selecionarConvidado = (participanteId) => {
    setUsuarioSelecionado(participanteId);
    setHorarioSelecionado("");
  };

  const handleCriarReuniao = async () => {
    const idInteressado =
      patrocinador?.id_usuario ?? patrocinador?.idUsuario ?? null;

    if (!idInteressado) {
      toast.error("Patrocinador sem usuário vinculado.");
      return;
    }
    if (!usuarioSelecionado) {
      toast.warning("Selecione um convidado.");
      return;
    }
    if (!horarioSelecionado) {
      toast.warning("Selecione um horário disponível.");
      return;
    }

    const slotSelecionado =
      slotsDoDia.find((slot) => {
        const inicio = normalizarHora(slot.horario_inicio);
        return inicio === horarioSelecionado;
      }) || null;

    setIsSaving(true);
    try {
      const payload = {
        id_interessado: Number(idInteressado),
        id_convidado: Number(usuarioSelecionado),
        horario: horarioSelecionado,
        dia: diaSelecionado,
        data: dataSelecionada,
      };
      if (slotSelecionado?.id) {
        payload.id_slot = slotSelecionado.id;
      }

      const resp = await api.post("/reunioes", payload);
      if (resp.status === 200) {
        if (resp.data?.status === "erro") {
          toast.error(resp.data?.mensagem || "Não foi possível criar a reunião.");
          return;
        }
        toast.success(resp.data?.mensagem || "Reunião criada com sucesso!");
        navigate("/private/adm/recepcao");
      }
    } catch (err) {
      console.error("Erro ao criar reunião:", err);
      toast.error(
        err?.response?.data?.mensagem ||
          "Erro ao criar a reunião. Tente novamente."
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="apl-page apl-page--padded irn-page">
      <header className="apl-header" aria-label="Cabeçalho nova reunião">
        <div className="apl-header-text">
          <h1>Nova reunião de negócio</h1>
        </div>

        <div className="apl-toolbar apl-toolbar--inline irn-toolbar">
          {!isLoadingMeta && diasComSlots.length > 0 ? (
            <nav className="apl-day-tabs irn-day-tabs" aria-label="Dias com slots">
              {diasComSlots.map((item) => {
                const qtd = slots.filter((s) => s.data === item.data).length;
                return (
                  <button
                    key={item.data}
                    type="button"
                    className={`apl-day-tab${
                      dataSelecionada === item.data ? " is-active" : ""
                    }`}
                    onClick={() => setDataSelecionada(item.data)}
                    disabled={isLoadingMeta || isSaving}
                  >
                    {item.label}
                    <span className="irn-day-count">{qtd}</span>
                  </button>
                );
              })}
            </nav>
          ) : (
            <div className="irn-day-tabs-spacer" aria-hidden />
          )}

          <div className="apl-toolbar-actions">
            <button
              type="button"
              className="apl-btn apl-btn--primary"
              onClick={handleCriarReuniao}
              disabled={!podeCriar || isLoadingMeta || isLoadingLista}
            >
              {isSaving ? (
                <FaSpinner className="fa-spin" size={14} aria-hidden />
              ) : (
                <FaCalendarPlus size={14} aria-hidden />
              )}
              {isSaving ? "Criando…" : "Criar reunião"}
            </button>
            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={() => navigate(-1)}
              disabled={isSaving}
            >
              <FaArrowLeft size={14} aria-hidden />
              Voltar
            </button>
          </div>
        </div>
      </header>

      <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
        {error ? (
          <div className="apl-alert" role="alert">
            {error}
          </div>
        ) : null}

        {isLoadingMeta ? (
          <div className="apl-card">
            <div className="apl-loading">
              <FaSpinner className="fa-spin" size={28} aria-hidden />
              <span>Carregando dias e slots…</span>
            </div>
          </div>
        ) : diasComSlots.length === 0 ? (
          <div className="apl-card">
            <div className="apl-empty">
              <p className="apl-empty-title">Nenhum slot cadastrado</p>
              <p style={{ margin: 0 }}>
                Cadastre slots no evento vigente para marcar reuniões.
              </p>
            </div>
          </div>
        ) : (
          <div className="irn-grid">
            <section className="apl-card irn-panel" aria-label="Convidados">
              <div className="irn-panel-head">
                <h2 className="irn-panel-title">Convidados</h2>
                <div className="apl-search irn-search">
                  <span className="apl-search-icon" aria-hidden>
                    <FaSearch size={14} />
                  </span>
                  <input
                    type="search"
                    className="apl-search-input"
                    placeholder="Buscar por nome…"
                    value={pesquisaNome}
                    onChange={(e) => setPesquisaNome(e.target.value)}
                    disabled={isLoadingLista || isSaving}
                    aria-label="Buscar convidado"
                  />
                </div>
              </div>

              {isLoadingLista ? (
                <div className="apl-loading irn-panel-loading">
                  <FaSpinner className="fa-spin" size={24} aria-hidden />
                  <span>Carregando convidados…</span>
                </div>
              ) : listaFiltrada.length === 0 ? (
                <div className="apl-empty">
                  <p className="apl-empty-title">Nenhum convidado encontrado</p>
                  <p style={{ margin: 0 }}>
                    Ajuste a busca ou selecione outro dia.
                  </p>
                </div>
              ) : (
                <ul className="irn-guest-list" aria-label="Lista de convidados">
                  {listaFiltrada.map((p) => {
                    const ativo = usuarioSelecionado === p.id;
                    const qtdSlots = (p.horariosSelecionaveis || []).length;
                    return (
                      <li key={p.id}>
                        <button
                          type="button"
                          className={`irn-guest-item${ativo ? " is-active" : ""}`}
                          onClick={() => selecionarConvidado(p.id)}
                          disabled={isSaving || p.lotado}
                          title={
                            p.lotado
                              ? "Convidado atingiu o limite de reuniões"
                              : undefined
                          }
                        >
                          <div className="irn-guest-main">
                            <span className="irn-guest-name">{p.nome || "—"}</span>
                            <span className="irn-guest-meta">
                              {[p.empresa, p.cargo].filter(Boolean).join(" · ") ||
                                "Sem instituição"}
                            </span>
                          </div>
                          <span
                            className={`irn-guest-badge${
                              qtdSlots === 0 ? " is-muted" : ""
                            }`}
                          >
                            {p.lotado
                              ? "Lotado"
                              : qtdSlots === 0
                                ? "Sem horário"
                                : `${qtdSlots} slot${qtdSlots === 1 ? "" : "s"}`}
                          </span>
                          {ativo ? (
                            <FaCheck className="irn-guest-check" aria-hidden />
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section className="apl-card irn-panel" aria-label="Horários">
              <div className="irn-panel-head">
                <h2 className="irn-panel-title">Horários disponíveis</h2>
              </div>

              {!convidadoSelecionado ? (
                <div className="apl-empty">
                  <p className="apl-empty-title">Selecione um convidado</p>
                  <p style={{ margin: 0 }}>
                    Escolha alguém na lista ao lado para ver os slots livres em{" "}
                    <strong>{labelDiaAtivo}</strong>.
                  </p>
                </div>
              ) : (
                <>
                  <div className="irn-selected">
                    <span className="irn-selected-label">Convidado</span>
                    <strong>{convidadoSelecionado.nome}</strong>
                    {convidadoSelecionado.empresa ? (
                      <span className="irn-selected-meta">
                        {convidadoSelecionado.empresa}
                      </span>
                    ) : null}
                  </div>

                  {horariosSelecionaveis.length === 0 ? (
                    <div className="apl-empty">
                      <p className="apl-empty-title">Sem horários neste dia</p>
                      <p style={{ margin: 0 }}>
                        O convidado ou a cota do patrocinador não permitem
                        marcação em {labelDiaAtivo}.
                      </p>
                    </div>
                  ) : (
                    <div
                      className="irn-slot-picker"
                      role="listbox"
                      aria-label="Horários disponíveis"
                    >
                      {horariosSelecionaveis.map((h) => {
                        const ocupados = ocupacaoPatrocinador[h] || 0;
                        return (
                          <button
                            key={h}
                            type="button"
                            role="option"
                            aria-selected={horarioSelecionado === h}
                            className={`irn-slot-option${
                              horarioSelecionado === h ? " is-selected" : ""
                            }`}
                            onClick={() => setHorarioSelecionado(h)}
                            disabled={isSaving}
                          >
                            <span className="irn-slot-time">{h}</span>
                            {cota > 0 ? (
                              <span className="irn-slot-capa">
                                {ocupados}/{cota}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <div className="irn-actions">
                    <button
                      type="button"
                      className="apl-btn apl-btn--primary"
                      onClick={handleCriarReuniao}
                      disabled={!podeCriar}
                    >
                      {isSaving ? (
                        <FaSpinner className="fa-spin" size={14} aria-hidden />
                      ) : (
                        <FaCalendarPlus size={14} aria-hidden />
                      )}
                      {isSaving ? "Criando…" : "Criar reunião"}
                    </button>
                  </div>
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncluirReuniaoNegocio;
